import {
  GLOBAL_SEARCH_BROWSE_LIMIT,
  GLOBAL_SEARCH_QUERY_KEY,
} from "@frontend-shared/constants";
import type { IGlobalSearchResponse } from "@frontend-shared/types";
import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import { globalSearchApi } from "@/api/services";
import { useAuth } from "@/hooks/use-auth";

interface IUseGlobalSearchBrowseProps {
  enabled: boolean;
}

// NOTE: An object, not a string: useGlobalSearch keys a search by its query
// string in the same position, so a string here would share a cache entry
// with a search for that very word.
const GLOBAL_SEARCH_BROWSE_KEY = { browse: true } as const;
const GLOBAL_SEARCH_BROWSE_STALE_TIME_MS = 60_000;

// NOTE: NSAI-6853 — GET /search/global?browse=true: every source's default
// listing, for the category tabs before anything is typed. One request serves
// every tab (each narrows it on the device), and it is only sent once a
// category tab is opened, then cached per project for a minute.
export const useGlobalSearchBrowse = ({
  enabled,
}: IUseGlobalSearchBrowseProps): UseQueryResult<IGlobalSearchResponse> => {
  const { currentProjectUuid } = useAuth();

  return useQuery({
    queryKey: [
      GLOBAL_SEARCH_QUERY_KEY,
      currentProjectUuid,
      GLOBAL_SEARCH_BROWSE_KEY,
    ],
    queryFn: () =>
      globalSearchApi.search({
        query: "",
        browse: true,
        limit: GLOBAL_SEARCH_BROWSE_LIMIT,
      }),
    enabled,
    staleTime: GLOBAL_SEARCH_BROWSE_STALE_TIME_MS,
    retry: false,
  });
};
